<script setup lang="ts">
import { computed, useAttrs } from 'vue'

import { useUiKitLink } from '@ui/composables/useUiKitLink'

import AppIcon from '@ui/app-icon/AppIcon.vue'

import type { AppButtonEmits, AppButtonProps } from './types'

const props = withDefaults(defineProps<AppButtonProps>(), {
  type: 'button',
  variant: 'accent',
  size: 'md',
  disabled: false,
  loading: false,
  leftIcon: '',
  rightIcon: '',
  block: false,
  spaced: 'default',
  to: '',
  target: '_blank',
})

const emit = defineEmits<AppButtonEmits>()
const externalAttrs = useAttrs()

const classes = computed(() => [
  `variant-${props.variant}`,
  `size-${props.size}`,
  { disabled: props.disabled || props.loading },
  { loading: props.loading },
  { block: props.block },
  props.spaced !== 'default' && `spaced-${props.spaced}`,
])

const { linkComponent } = useUiKitLink()

const isDisabled = computed(() => props.disabled || props.loading)
const isLinkDisabled = computed(() => Boolean(props.to) && isDisabled.value)

const component = computed(() => {
  if (props.to?.startsWith('http') || isLinkDisabled.value) {
    return 'a'
  }

  return props.to ? linkComponent || 'a' : 'button'
})

const fontClasses = computed(() => {
  const map: Record<string, string> = {
    lg: 'font-large-link',
  }
  return map[props.size] || 'font-medium-link'
})

const iconSize = computed(() => {
  const map: Record<string, string> = {
    lg: '20px',
  }
  return map[props.size] || '16px'
})

const attrs = computed(() => {
  const extra: Record<string, string> = {}

  if (isLinkDisabled.value) {
    extra.role = 'link'
  } else if (props.to?.startsWith('http')) {
    extra.href = props.to
    extra.target = props.target
    extra.rel = 'noopener noreferrer'
  } else if (props.to && linkComponent) {
    extra.to = props.to
  } else if (props.to) {
    extra.href = props.to
  }

  return {
    ...externalAttrs,
    ...extra,
  }
})

const buttonType = computed(() => (props.to ? undefined : props.type))
const nativeDisabled = computed(() => (props.to ? undefined : isDisabled.value))

function handleClick(event: MouseEvent) {
  if (isDisabled.value) {
    event.preventDefault()
    return
  }

  emit('click', event)
}
</script>

<template>
  <component
    :is="component"
    class="app-button"
    :class="classes"
    :disabled="nativeDisabled"
    :type="buttonType"
    :aria-busy="loading"
    :aria-disabled="isDisabled || undefined"
    data-test-id="app-button"
    v-bind="attrs"
    @click="handleClick"
  >
    <span class="app-button-body" :class="fontClasses">
      <slot name="left-icon">
        <app-icon
          v-if="leftIcon"
          class="app-button-prepend-icon"
          :name="leftIcon"
          :size="iconSize"
        />
      </slot>
      <span
        v-if="$slots.default"
        class="app-button-content"
        data-element="app-button-content"
      >
        <slot />
      </span>
      <slot name="right-icon">
        <app-icon
          v-if="rightIcon"
          class="app-button-append-icon"
          :name="rightIcon"
          :size="iconSize"
        />
      </slot>
    </span>
  </component>
</template>

<style lang="scss" scoped>
.app-button {
  --gap: 8px;
  --icon-size: 16px;
  --border-radius: 12px;
  --spinner-color: var(--absolute-white);

  $root: &;

  position: relative;
  margin: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--border-radius);
  background-color: transparent;
  outline: 2px solid transparent;
  outline-offset: 2px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out,
    opacity 0.2s ease-in-out;

  &-body {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;
    gap: var(--gap);
  }

  &-prepend-icon,
  &-append-icon {
    font-size: var(--icon-size);
  }

  &-content {
    display: flex;
    align-items: center;
    min-width: 0;
    gap: var(--gap);

    &:empty {
      display: none;
    }
  }

  &:where(.spaced-left, .spaced-right, .spaced-both) {
    #{$root}-body {
      flex: 1;
    }

    #{$root}-content {
      flex: 1;
    }
  }

  &:where(.spaced-left) #{$root}-content {
    justify-content: flex-end;
    text-align: right;
  }

  &:where(.spaced-right) #{$root}-content {
    justify-content: flex-start;
    text-align: left;
  }

  &:where(.spaced-both) #{$root}-content {
    justify-content: center;
    text-align: center;
  }

  &:where(.variant-menu) {
    --gap: 12px;
    --icon-size: 20px;
    --border-radius: 10px;
    --spinner-color: var(--g-neutral-2);

    padding: 0 12px;
    color: var(--g-neutral-1);

    #{$root}-body {
      line-height: 24px;
    }

    #{$root}-prepend-icon,
    #{$root}-append-icon {
      color: var(--g-neutral-3);
    }

    &:hover {
      background-color: var(--g-neutral-6);
    }

    &.disabled {
      color: var(--g-neutral-4);

      #{$root}-prepend-icon,
      #{$root}-append-icon {
        color: var(--g-neutral-4);
      }
    }
  }

  &:where(.variant-accent) {
    background-color: var(--a-mulberry);
    color: var(--absolute-white);

    &:hover {
      background-color: var(--a-mulberry-light);
    }

    &.disabled {
      background-color: var(--a-mulberry-fade-100);
    }
  }

  &:where(.variant-primary) {
    --spinner-color: var(--g-neutral-8);

    background-color: var(--g-neutral-1);
    color: var(--g-neutral-8);

    &:hover {
      background-color: var(--g-neutral-2);
    }

    &.disabled {
      background-color: var(--g-neutral-5);
    }
  }

  &:where(.variant-secondary) {
    --spinner-color: var(--g-neutral-2);

    background-color: var(--g-neutral-6);
    color: var(--g-neutral-2);

    &:hover {
      background-color: var(--g-neutral-5);
    }

    &.disabled {
      background-color: var(--g-neutral-6);
      color: var(--g-neutral-4);
    }
  }

  &:where(.variant-tertiary) {
    --spinner-color: var(--g-neutral-2);

    border-color: var(--g-neutral-5);
    color: var(--g-neutral-2);

    &:hover {
      border-color: var(--g-neutral-3);
    }

    &:focus-visible {
      border-color: transparent;
    }

    &.disabled {
      border-color: var(--g-neutral-5);
      color: var(--g-neutral-4);
    }
  }

  &:where(.variant-text) {
    --spinner-color: var(--g-neutral-2);

    border-color: transparent;
    background-color: transparent;
    color: var(--g-neutral-1);

    &:hover {
      opacity: 0.7;
    }

    &.disabled {
      color: var(--g-neutral-4);
      opacity: 1;
    }
  }

  &:where(.variant-red) {
    background-color: var(--s-red);
    color: var(--absolute-white);

    &:hover {
      opacity: 0.8;
    }

    &.disabled {
      opacity: 0.55;
    }
  }

  &:where(.variant-white) {
    --spinner-color: var(--g-neutral-2);

    border-color: var(--absolute-white);
    background-color: var(--absolute-white);
    color: var(--absolute-black);

    &:hover {
      border-color: var(--light-gray);
      background-color: var(--light-gray);
    }

    &:focus-visible {
      border-color: transparent;
    }

    &.disabled {
      border-color: var(--g-neutral-5);
      background-color: var(--g-neutral-5);
      color: var(--g-neutral-4);
    }
  }

  &:where(.size-lg) {
    --icon-size: 20px;

    height: 52px;
    padding: 0 24px;
  }

  &:where(.size-md) {
    height: 44px;
    padding: 0 20px;
  }

  &:where(.size-sm) {
    --border-radius: 10px;

    height: 36px;
    padding: 0 16px;
  }

  &:where(.size-xs) {
    --gap: 6px;
    --border-radius: 8px;

    height: 28px;
    padding: 0 8px;
  }

  &:focus-visible {
    outline-color: var(--a-lilac);
  }

  &:where(.block) {
    width: 100%;
  }

  &:where(.disabled) {
    cursor: not-allowed;
  }

  &.loading {
    color: transparent;
    cursor: progress;

    &::before {
      position: absolute;
      inset: 0;
      margin: auto;
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2px solid var(--spinner-color);
      border-radius: 50%;
      animation: app-spin var(--app-spin-duration) linear infinite;
      border-bottom-color: transparent;
      content: '';
    }
  }
}
</style>
